import { useRoute } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Check } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useMemo, useState } from "react";
import { XStack } from "tamagui";
import { Body } from "../../ui/Body";
import { Group } from "../../ui/Group";
import { Sheet, type SheetProps } from "../../ui/Sheet";
import { Title } from "../../ui/Title";
import { UserAvatar } from "../../ui/UserAvatar";
import { useStores } from "../stores";

export const useUserSelectSheet = (
  onUserSelect: (userUid: string) => void,
  selectedUserUid?: string,
) => {
  const [isOpen, setOpen] = useState(false);

  return useMemo(() => {
    return {
      setOpen,
      UserSelectSheet: () => (
        <UserSelectSheet
          sheetId="user_select"
          open={isOpen}
          onOpenChange={(open: boolean) => setOpen(open)}
          onUserSelect={onUserSelect}
          onClose={() => setOpen(false)}
          selectedUserUid={selectedUserUid}
        />
      ),
    };
  }, [isOpen, onUserSelect, selectedUserUid]);
};

export const UserSelectSheet: React.FC<
  SheetProps & {
    header?: string;
    onUserSelect: (userUid: string) => void;
    onClose: () => void;
    selectedUserUid?: string;
  }
> = observer(
  ({ header, onUserSelect, onClose, selectedUserUid, ...sheetProps }) => {
    const route =
      useRoute<
        NativeStackScreenProps<ReactNavigation.RootParamList, "Module">["route"]
      >();

    const { modulesStore } = useStores();
    const module = modulesStore.byId.get(route.params.moduleUid);

    if (!module) {
      return null;
    }

    return (
      <Sheet header={<Title>{header ?? "Select user"}</Title>} {...sheetProps}>
        <Group borderTop={false}>
          {module.plan.users.map((user) => {
            const inputKey = `split-${user.uid}`;
            return (
              <XStack
                key={inputKey}
                width={"100%"}
                gap={"$medium"}
                alignItems="center"
                onPress={(_e) => {
                  onUserSelect(user.uid);
                  onClose();
                }}
              >
                {selectedUserUid === user.uid ? <Check /> : undefined}
                <UserAvatar userUid={user.uid} />
                <Body>{user.data.displayName}</Body>
              </XStack>
            );
          })}
        </Group>
      </Sheet>
    );
  },
);
